/**
 * Amplitude Analytics and Session Replay initialization
 * 
 * This module ensures:
 * - Amplitude is only initialized once during the application lifecycle
 * - Code only runs on the client-side (not during SSR)
 * - Session Replay is enabled with privacy-safe settings
 * 
 * Privacy: user-generated content (chat messages, images, videos)
 * is excluded from Amplitude via conservative masking + media blocking
 */

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { hasAnalyticsConsent } from './cookieConsent';

// Flag to ensure Amplitude is initialized only once
let isInitialized = false;

/**
 * Initialize Amplitude Analytics with Session Replay
 * Safe to call multiple times - will only initialize once.
 *
 * On the .ru segment Amplitude is gated behind explicit cookie consent
 * (`analytics: true`). On .com it stays on by default (legacy behaviour).
 */
export function initAmplitude(): void {
  // Guard: Only run on client-side
  if (typeof window === 'undefined') {
    return;
  }

  // Guard: Only initialize once
  if (isInitialized) {
    return;
  }

  // Guard: respect cookie consent on the .ru segment.
  // On .com `hasAnalyticsConsent()` returns true unconditionally.
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    // Add Session Replay plugin with privacy-safe configuration
    // - conservative: masks ALL text and form fields in replays
    // - blockSelector: replaces media elements with blank placeholders
    amplitude.add(sessionReplayPlugin({
      sampleRate: 1,
      privacyConfig: {
        defaultMaskLevel: 'conservative',
        blockSelector: [
          'img',
          'video',
          'audio',
          'canvas',
          'svg image',
          '[data-amp-block]',
          '.amp-block',
        ],
      },
    }));

    // Initialize Amplitude with selective autocapture
    // - Disabled: formInteractions (captures input text),
    //             elementInteractions (captures clicked element text),
    //             fileDownloads (captures file URLs)
    // - Enabled:  sessions, pageViews, attribution (safe metadata only)
    amplitude.init('167f4dcb88a4c826beb2b8e88f960ab9', {
      autocapture: {
        sessions: true,
        pageViews: true,
        attribution: true,
        fileDownloads: false,
        formInteractions: false,
        elementInteractions: false,
      },
      serverZone: 'EU',
    });

    isInitialized = true;
    console.log('Amplitude Analytics initialized (privacy-safe mode)');
  } catch (error) {
    console.warn('Failed to initialize Amplitude:', error);
  }
}

/**
 * Stop sending events to Amplitude.
 * Called when the user revokes analytics consent through the cookie banner.
 * Amplitude SDK supports opt-out: queued events are dropped, future calls become no-ops.
 */
export function disableAmplitude(): void {
  if (typeof window === 'undefined') return;
  try {
    amplitude.setOptOut(true);
  } catch (error) {
    console.warn('Failed to opt-out of Amplitude:', error);
  }
}

// Export amplitude instance for custom event tracking if needed
export { amplitude };
